



//  update 


// src/components/admin/pages/TasksPage.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchTasks, 
  createTask, 
  updateTaskStatus, 
  deleteTask,
  updateTask 
} from '../../../redux/slices/tasksSlice';
import { fetchEmployees } from '../../../redux/slices/adminEmployeesSlice';
import { fetchProjects } from '../../../redux/slices/projectsSlice'; // ✅ ADD THIS LINE
import './TasksPage.css';

const TasksPage = ({ currentUser }) => {
  const dispatch = useDispatch();
  
  // ✅ REDUX STATES
  const { tasks: tasksFromAPI, loading, error } = useSelector(state => state.tasks);
  const { employees, employeesLoading } = useSelector(state => state.adminEmployees);
  const { projects: projectsFromRedux } = useSelector(state => state.projects);
  
  // ✅ LOCAL STATES
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  
  const [filters, setFilters] = useState({
    projectName: '',
    status: '',
    dateRange: { start: '', end: '' }
  });

  const contentRef = useRef(null);

  // ✅ FETCH DATA FROM API
  useEffect(() => {
    dispatch(fetchTasks());
    dispatch(fetchEmployees());
    dispatch(fetchProjects());
  }, [dispatch]);

  // ✅ API DATA को filteredTasks में update करें
  useEffect(() => {
    if (tasksFromAPI && tasksFromAPI.length > 0) {
      setFilteredTasks(tasksFromAPI);
    } else {
      setFilteredTasks([]);
    }
  }, [tasksFromAPI]);

  // ✅ APPLY FILTERS (API data पर)
  useEffect(() => {
    const applyFilters = () => {
      if (!tasksFromAPI) return;
      
      let result = [...tasksFromAPI];
      
      // Project name filter
      if (filters.projectName) {
        const searchTerm = filters.projectName.toLowerCase();
        result = result.filter(task => 
          (task.project && task.project.toLowerCase().includes(searchTerm)) ||
          (task.title && task.title.toLowerCase().includes(searchTerm))
        );
      }
      
      // Status filter
      if (filters.status) {
        result = result.filter(task => task.status === filters.status);
      }
      
      // Date range filter
      if (filters.dateRange.start && filters.dateRange.end) {
        result = result.filter(task => {
          if (!task.dueDate) return true;
          const dueDate = new Date(task.dueDate);
          const start = new Date(filters.dateRange.start);
          const end = new Date(filters.dateRange.end);
          return dueDate >= start && dueDate <= end;
        });
      }
      
      setFilteredTasks(result);
    };
    
    const timer = setTimeout(applyFilters, 300);
    return () => clearTimeout(timer);
  }, [filters, tasksFromAPI]);

  // ✅ FILTER HANDLERS
  const handleFilterChange = useCallback((name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleDateRangeChange = useCallback((start, end) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { start, end }
    }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      projectName: '',
      status: '',
      dateRange: { start: '', end: '' }
    });
  }, []);

  // ✅ CREATE NEW TASK WITH API
  // const handleCreateTask = async (taskData) => {
  //   try {
  //     const result = await dispatch(createTask(taskData));
  //     if (result?.payload) {
  //       alert("Task Created Successfully");
  //       setShowCreateForm(false);
  //     } else {
  //       alert("Task Create Failed");
  //     }
  //   } catch (err) {
  //     alert("Error creating task: " + err.message);
  //   }
  // };


  // ✅ Get formatted projects for dropdown
const projectOptions = useMemo(() => {
  if (!projectsFromRedux || !Array.isArray(projectsFromRedux)) return [];
  
  return projectsFromRedux.map(project => ({
    id: project._id,
    name: project.name || project.title || 'Unnamed Project',
    client: project.client || 'No Client'
  }));
}, [projectsFromRedux]);

  // ✅ CREATE NEW TASK WITH API
// const handleCreateTask = async (taskData) => {
//   try {
//     console.log('Creating task with data:', taskData); // Debugging के लिए
    
//     const result = await dispatch(createTask(taskData));
    
//     if (result?.payload) {
//       alert("Task Created Successfully");
//       setShowCreateForm(false);
      
//       // Refresh tasks list
//       dispatch(fetchTasks());
//     } else {
//       alert("Task Create Failed: " + (result?.error?.message || 'Unknown error'));
//     }
//   } catch (err) {
//     alert("Error creating task: " + err.message);
//   }
// };


// ✅ CREATE NEW TASK WITH API
const handleCreateTask = async (taskData) => {
  try {
    console.log('🎯 Creating task with data:', taskData);
    
    // Show warning if multiple users selected
    if (taskData.assignedUsers.length > 1) {
      const confirm = window.confirm(
        '⚠️ Note: Currently only one user can be assigned to a task.\n' +
        'Only the first selected user will be assigned.\n\n' +
        'Continue?'
      );
      if (!confirm) return;
    }
    
    const result = await dispatch(createTask(taskData));
    
    if (createTask.fulfilled.match(result)) {
      alert("✅ Task Created Successfully!");
      setShowCreateForm(false);
      
      // Refresh tasks list
      dispatch(fetchTasks());
    } else if (createTask.rejected.match(result)) {
      console.error('❌ Task create rejected:', result);
      alert(`❌ Task Create Failed: ${result.error?.message || result.payload || "Unknown error"}`);
    }
  } catch (err) {
    console.error('💥 Error in handleCreateTask:', err);
    alert("💥 Error creating task: " + err.message);
  }
};

  // ✅ EDIT TASK FUNCTION
  const handleEditTask = useCallback((task) => {
    setEditingTask(task);
    setShowEditForm(true);
  }, []);

  // ✅ UPDATE TASK FUNCTION
  const handleUpdateTask = async (taskData) => {
    try {
      const result = await dispatch(updateTask({
        id: editingTask._id,
        taskData
      }));
      if (result?.payload) {
        alert("Task Updated Successfully");
        setShowEditForm(false);
        setEditingTask(null);
      } else {
        alert("Task Update Failed");
      }
    } catch (err) {
      alert("Error updating task: " + err.message);
    }
  };

  // ✅ DELETE TASK WITH API
  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        const result = await dispatch(deleteTask(taskId));
        if (result?.payload) {
          alert("Task Deleted Successfully");
        } else {
          alert("Delete Failed");
        }
      } catch (err) {
        alert("Error deleting task: " + err.message);
      }
    }
  };

  // ✅ VIEW TASK DETAILS
  const handleViewTask = (task) => {
    setSelectedTask(task);
  };

  // ✅ UPDATE TASK STATUS
  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      const result = await dispatch(updateTaskStatus({ id: taskId, status: newStatus }));
      if (result?.payload) {
        alert("Status Updated Successfully");
      }
    } catch (err) {
      alert("Error updating status: " + err.message);
    }
  };

  // ✅ EXPORT TO CSV
  const handleExportCSV = useCallback(() => {
    if (!filteredTasks || filteredTasks.length === 0) {
      alert("No tasks to export");
      return;
    }
    
    const headers = ['Task', 'Project', 'Status', 'Priority', 'Due Date', 'Progress', 'Assigned Users'];
    const csvContent = [
      headers.join(','),
      ...filteredTasks.map(task => [
        `"${task.title || ''}"`,
        `"${task.project || ''}"`,
        task.status || '',
        task.priority || '',
        task.dueDate || '',
        `${task.progress || 0}%`,
        `"${task.assignedUsers?.map(u => u.name).join(', ') || ''}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tasks_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, [filteredTasks]);

  // ✅ FORMAT DATE
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }, []);

  // ✅ GET STATUS COLOR
  const getStatusColor = useCallback((status) => {
    const statusColors = {
      'Testing Phase': '#3b82f6',
      'In Progress': '#10b981',
      'Completed': '#059669',
      'Blocked': '#ef4444',
      'Planning': '#8b5cf6',
      'On Hold': '#f59e0b',
      'Delayed': '#f97316',
      'Pending': '#64748b',
      'Cancelled': '#6b7280'
    };
    return statusColors[status] || '#94a3b8';
  }, []);

  // ✅ GET PRIORITY COLOR
  const getPriorityColor = useCallback((priority) => {
    const priorityColors = {
      'High': '#ef4444',
      'Medium': '#f59e0b',
      'Low': '#10b981'
    };
    return priorityColors[priority] || '#94a3b8';
  }, []);

  // ✅ GET USER INITIALS
  const getUserInitials = useCallback((userName) => {
    if (!userName) return 'NA';
    return userName.split(' ').map(n => n.charAt(0)).join('').toUpperCase();
  }, []);

  // ✅ STATUS OPTIONS
  const statusOptions = useMemo(() => [
    '', 'In Progress', 'Testing Phase', 'Completed', 'Blocked', 
    'Planning', 'On Hold', 'Delayed', 'Pending', 'Cancelled'
  ], []);

  // ✅ CALCULATE STATS (API data से)
  const stats = useMemo(() => {
    const tasks = tasksFromAPI || [];
    return {
      total: tasks.length,
      inProgress: tasks.filter(t => t.status === 'In Progress').length,
      completed: tasks.filter(t => t.status === 'Completed').length,
      highPriority: tasks.filter(t => t.priority === 'High').length,
      overdue: tasks.filter(t => {
        if (!t.dueDate) return false;
        return new Date(t.dueDate) < new Date() && t.status !== 'Completed';
      }).length,
      avgProgress: tasks.length > 0 
        ? Math.round(tasks.reduce((acc, t) => acc + (t.progress || 0), 0) / tasks.length)
        : 0
    };
  }, [tasksFromAPI]);

  // ✅ HANDLE USER CLICK
  const handleUserClick = useCallback((userName) => {
    const user = employees?.find(e => e.name === userName);
    if (user) {
      setSelectedUser(user);
    }
  }, [employees]);

  // Loading और Error states
  if (loading && !tasksFromAPI) {
    return (
      <div className="tasks-page-container">
        <div className="tasks-loading-container">
          <div className="tasks-loading-spinner"></div>
          <p className="tasks-loading-text">Loading tasks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tasks-page-container">
        <div className="error-container">
          <span className="error-icon">⚠️</span>
          <h4>Error Loading Tasks</h4>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={() => dispatch(fetchTasks())}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tasks-page-container">
      {/* Header */}
      <div className="tasks-page-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon">📋</div>
            <div className="header-title">
              <h1>Tasks Management</h1>
              <p>
                Track and manage all tasks • Total: 
                <span className="count-badge">{tasksFromAPI?.length || 0} Tasks</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tasks-main-content" ref={contentRef}>
        {/* Action Bar */}
        <div className="tasks-action-bar">
          <button 
            className="create-task-btn"
            onClick={() => setShowCreateForm(true)}
          >
            <span className="btn-icon">+</span>
            Create New Task
          </button>

          <div className="tasks-view-toggle">
            <button 
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <span className="view-toggle-icon">📋</span>
              List View
            </button>
            <button 
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <span className="view-toggle-icon">🔲</span>
              Grid View
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="tasks-filters-section">
          <div className="filters-title">
            <span>Filters</span>
            {Object.values(filters).some(filter => filter !== '') && (
              <button 
                onClick={handleClearFilters}
                className="clear-all-filters-btn"
              >
                Clear All
              </button>
            )}
          </div>
          <div className="filters-row">
            <div className="filter-group">
              <label>Search Tasks/Projects</label>
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder="Search tasks or projects..."
                  value={filters.projectName}
                  onChange={(e) => handleFilterChange('projectName', e.target.value)}
                  className="filter-input"
                />
                <span className="search-icon">🔍</span>
              </div>
            </div>
            
            <div className="filter-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="filter-select"
              >
                <option value="">All Status</option>
                {statusOptions.slice(1).map(status => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label>Due Date Range</label>
              <div className="date-range-wrapper">
                <input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateRangeChange(e.target.value, filters.dateRange.end)}
                  className="date-input"
                  placeholder="Start Date"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateRangeChange(filters.dateRange.start, e.target.value)}
                  className="date-input"
                  placeholder="End Date"
                />
              </div>
            </div>
            
            <button 
              className="clear-filters-btn"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="results-info">
          <span className="results-count">
            Showing {filteredTasks.length} of {tasksFromAPI?.length || 0} tasks
          </span>
          <button 
            className="export-csv-btn"
            onClick={handleExportCSV}
            title="Export to CSV"
            disabled={!filteredTasks || filteredTasks.length === 0}
          >
            <span className="export-icon">📊</span>
            Export CSV
          </button>
        </div>

        {/* Tasks Table/Grid */}
        {viewMode === 'list' ? (
          <div className="tasks-table-container">
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Project</th>
                  <th>Users</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!filteredTasks || filteredTasks.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="no-data">
                      <div className="no-tasks-found">
                        <span className="no-data-icon">📋</span>
                        <h4>No tasks found</h4>
                        <p>Try adjusting your filters or create a new task</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task) => (
                    <tr key={task._id}>
                      <td>
                        <div className="task-title-cell">
                          <div className="task-title">{task.title || 'Untitled Task'}</div>
                          <div className="task-description">{task.description || 'No description'}</div>
                        </div>
                      </td>
                      <td>
                        <div className="project-cell">{task.project || 'No Project'}</div>
                      </td>
                      <td>
                        <div className="users-cell">
                          {task.assignedUsers && task.assignedUsers.slice(0, 3).map((user, index) => (
                            <span 
                              key={index} 
                              className="user-avatar" 
                              title={user.name}
                              onClick={() => handleUserClick(user.name)}
                            >
                              {getUserInitials(user.name)}
                            </span>
                          ))}
                          {task.assignedUsers && task.assignedUsers.length > 3 && (
                            <span className="user-more" title={`+${task.assignedUsers.length - 3} more`}>
                              +{task.assignedUsers.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span 
                          className="status-badge"
                          style={{ 
                            backgroundColor: `${getStatusColor(task.status)}20`,
                            color: getStatusColor(task.status),
                            borderColor: getStatusColor(task.status)
                          }}
                        >
                          {task.status || 'Pending'}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="priority-badge"
                          style={{ color: getPriorityColor(task.priority) }}
                        >
                          {task.priority || 'Medium'}
                        </span>
                      </td>
                      <td>
                        <div className={`due-date ${new Date(task.dueDate) < new Date() && task.status !== 'Completed' ? 'overdue' : ''}`}>
                          {formatDate(task.dueDate)}
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn view-btn" 
                            title="View Details"
                            onClick={() => handleViewTask(task)}
                          >
                            👁️
                          </button>
                          <button 
                            className="action-btn edit-btn" 
                            title="Edit Task"
                            onClick={() => handleEditTask(task)}
                          >
                            ✏️
                          </button>
                          {currentUser?.role === 'admin' && (
                            <button 
                              className="action-btn delete-btn" 
                              title="Delete Task"
                              onClick={() => handleDeleteTask(task._id)}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="tasks-grid-container">
            <div className="tasks-grid">
              {!filteredTasks || filteredTasks.length === 0 ? (
                <div className="no-tasks-grid">
                  <span className="no-data-icon">📋</span>
                  <h4>No tasks found</h4>
                  <p>Try adjusting your filters or create a new task</p>
                </div>
              ) : (
                filteredTasks.map((task) => (
                  <div key={task._id} className="task-card">
                    <div className="task-card-header">
                      <h4 className="task-title">{task.title || 'Untitled Task'}</h4>
                      <span 
                        className="task-priority"
                        style={{ color: getPriorityColor(task.priority) }}
                      >
                        {task.priority || 'Medium'}
                      </span>
                    </div>
                    
                    <div className="task-project">
                      <span className="project-icon">📁</span>
                      {task.project || 'No Project'}
                    </div>
                    
                    <div className="task-description">
                      {task.description || 'No description available'}
                    </div>
                    
                    <div className="task-users">
                      <div className="users-list">
                        {task.assignedUsers && task.assignedUsers.map((user, index) => (
                          <span 
                            key={index} 
                            className="user-avatar-small"
                            title={user.name}
                            onClick={() => handleUserClick(user.name)}
                          >
                            {getUserInitials(user.name)}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="task-status-row">
                      <span 
                        className="status-badge"
                        style={{ 
                          backgroundColor: `${getStatusColor(task.status)}20`,
                          color: getStatusColor(task.status)
                        }}
                      >
                        {task.status || 'Pending'}
                      </span>
                      <div className={`due-date ${new Date(task.dueDate) < new Date() && task.status !== 'Completed' ? 'overdue' : ''}`}>
                        {formatDate(task.dueDate)}
                      </div>
                    </div>
                    
                    <div className="task-actions">
                      <button className="view-btn" onClick={() => handleViewTask(task)}>View</button>
                      <button className="edit-btn" onClick={() => handleEditTask(task)}>Edit</button>
                      {currentUser?.role === 'admin' && (
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteTask(task._id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Stats Footer */}
        <div className="tasks-stats-footer">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{stats.inProgress}</div>
              <div className="stat-label">In Progress</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.completed}</div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.highPriority}</div>
              <div className="stat-label">High Priority</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.avgProgress}%</div>
              <div className="stat-label">Avg Progress</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.overdue}</div>
              <div className="stat-label">Overdue</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Tasks</div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Task Form Modal */}
      {showCreateForm && (
        <CreateTaskForm
          employees={employees || []}
          projects={projectOptions} 
          onCreate={handleCreateTask}
          onClose={() => setShowCreateForm(false)}
        />
      )}

      {/* Edit Task Form Modal */}
      {showEditForm && editingTask && (
        <EditTaskForm
          task={editingTask}
          employees={employees || []}
          projects={projectOptions}
          onUpdate={handleUpdateTask}
          onClose={() => {
            setShowEditForm(false);
            setEditingTask(null);
          }}
        />
      )}

      {/* Task Details Modal */}
      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          employees={employees || []}
          onClose={() => setSelectedTask(null)}
          onStatusUpdate={handleUpdateStatus}
        />
      )}

      {/* Employee Details Modal */}
      {selectedUser && (
        <EmployeeDetailsModal
          employee={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}
    </div>
  );
};

// ✅ CREATE TASK FORM COMPONENT
// const CreateTaskForm = ({ employees, onCreate, onClose }) => {
//   const [formData, setFormData] = useState({
//     title: '',
//     description: '',
//     project: '',
//     status: 'Planning',
//     priority: 'Medium',
//     startDate: '',
//     dueDate: '',
//     assignedUsers: []
//   });

//   const [projectSuggestions, setProjectSuggestions] = useState([]);

//   // Get unique projects from employees
//   useEffect(() => {
//     if (employees && employees.length > 0) {
//       const projects = [...new Set(employees.flatMap(emp => 
//         emp.projects?.map(p => p.name) || []
//       ))];
//       setProjectSuggestions(projects);
//     }
//   }, [employees]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };

//   const handleMultiSelect = (userId) => {
//     setFormData(prev => {
//       const currentValues = [...prev.assignedUsers];
//       const index = currentValues.indexOf(userId);
      
//       if (index === -1) {
//         currentValues.push(userId);
//       } else {
//         currentValues.splice(index, 1);
//       }
      
//       return { ...prev, assignedUsers: currentValues };
//     });
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
    
//     if (!formData.title || !formData.project || formData.assignedUsers.length === 0) {
//       alert('Please fill in all required fields');
//       return;
//     }
    
//     // Format data for API
//     const taskData = {
//       title: formData.title,
//       description: formData.description,
//       project: formData.project,
//       status: formData.status,
//       priority: formData.priority,
//       startDate: formData.startDate,
//       dueDate: formData.dueDate,
//       assignedUsers: formData.assignedUsers.map(userId => ({
//         userId,
//         name: employees.find(e => e._id === userId)?.name || 'Unknown'
//       }))
//     };
    
//     onCreate(taskData);
//   };

//   return (
//     <div className="create-task-overlay">
//       <div className="create-task-modal">
//         <div className="create-task-header">
//           <h2>Create New Task</h2>
//           <button onClick={onClose}>✕</button>
//         </div>
        
//         <form className="create-task-form" onSubmit={handleSubmit}>
//           <div className="form-group">
//             <label>Task Title *</label>
//             <input
//               type="text"
//               name="title"
//               value={formData.title}
//               onChange={handleInputChange}
//               placeholder="Enter task title"
//               required
//             />
//           </div>
          
//           <div className="form-group">
//             <label>Description</label>
//             <textarea
//               name="description"
//               value={formData.description}
//               onChange={handleInputChange}
//               placeholder="Enter task description"
//               rows="3"
//             />
//           </div>
          
//           <div className="form-group">
//             <label>Project Name *</label>
//             <input
//               type="text"
//               name="project"
//               value={formData.project}
//               onChange={handleInputChange}
//               placeholder="Enter project name"
//               required
//               list="project-suggestions"
//             />
//             <datalist id="project-suggestions">
//               {projectSuggestions.map((project, index) => (
//                 <option key={index} value={project} />
//               ))}
//             </datalist>
//           </div>
          
//           <div className="form-row">
//             <div className="form-group">
//               <label>Status</label>
//               <select
//                 name="status"
//                 value={formData.status}
//                 onChange={handleInputChange}
//               >
//                 <option value="Planning">Planning</option>
//                 <option value="In Progress">In Progress</option>
//                 <option value="Testing Phase">Testing Phase</option>
//                 <option value="Completed">Completed</option>
//                 <option value="Blocked">Blocked</option>
//                 <option value="On Hold">On Hold</option>
//               </select>
//             </div>
            
//             <div className="form-group">
//               <label>Priority</label>
//               <select
//                 name="priority"
//                 value={formData.priority}
//                 onChange={handleInputChange}
//               >
//                 <option value="Low">Low</option>
//                 <option value="Medium">Medium</option>
//                 <option value="High">High</option>
//               </select>
//             </div>
//           </div>
          
//           <div className="form-row">
//             <div className="form-group">
//               <label>Start Date</label>
//               <input
//                 type="date"
//                 name="startDate"
//                 value={formData.startDate}
//                 onChange={handleInputChange}
//               />
//             </div>
            
//             <div className="form-group">
//               <label>Due Date *</label>
//               <input
//                 type="date"
//                 name="dueDate"
//                 value={formData.dueDate}
//                 onChange={handleInputChange}
//                 required
//               />
//             </div>
//           </div>
          
//           <div className="form-group">
//             <label>Assign to Users *</label>
//             <div className="users-selection-grid">
//               {employees.map(employee => (
//                 <div 
//                   key={employee._id}
//                   className={`user-select-item ${formData.assignedUsers.includes(employee._id) ? 'selected' : ''}`}
//                   onClick={() => handleMultiSelect(employee._id)}
//                 >
//                   <div className="user-avatar-select">
//                     {employee.name?.split(' ').map(n => n.charAt(0)).join('') || 'NA'}
//                   </div>
//                   <div className="user-info-select">
//                     <div className="user-name-select">{employee.name}</div>
//                     <div className="user-role-select">{employee.role}</div>
//                   </div>
//                   <div className="user-checkbox">
//                     {formData.assignedUsers.includes(employee._id) ? '✓' : ''}
//                   </div>
//                 </div>
//               ))}
//             </div>
//             <div className="selected-count">
//               Selected: {formData.assignedUsers.length} user(s)
//             </div>
//           </div>
          
//           <div className="form-actions">
//             <button 
//               type="button"
//               className="cancel-btn"
//               onClick={onClose}
//             >
//               Cancel
//             </button>
//             <button 
//               type="submit"
//               className="submit-btn"
//             >
//               Create Task
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };



// CREATE TASK FORM COMPONENT में
// const CreateTaskForm = ({ employees, onCreate, onClose }) => {
//   const [formData, setFormData] = useState({
//     title: '',
//     description: '',
//     projectId: '', // project की जगह projectId
//     projectName: '', // UI display के लिए
//     status: 'Planning',
//     priority: 'Medium',
//     startDate: '',
//     dueDate: '',
//     assignedUsers: []
//   });

//   const [projectSuggestions, setProjectSuggestions] = useState([]);

//   // Employees से projects निकालें (अगर backend से projects नहीं मिलते)
//   useEffect(() => {
//     if (employees && employees.length > 0) {
//       const projects = [...new Set(employees.flatMap(emp => 
//         emp.projects?.map(p => ({ id: p._id, name: p.name })) || []
//       ))];
//       setProjectSuggestions(projects);
//     }
//   }, [employees]);

//   const handleProjectChange = (projectId, projectName) => {
//     setFormData(prev => ({ 
//       ...prev, 
//       projectId,
//       projectName 
//     }));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
    
//     if (!formData.title || !formData.projectId || formData.assignedUsers.length === 0) {
//       alert('Please fill in all required fields');
//       return;
//     }
    
//     // Format data for API - projectId भेजें
//     const taskData = {
//       title: formData.title,
//       description: formData.description,
//       projectId: formData.projectId, // Backend को projectId चाहिए
//       status: formData.status,
//       priority: formData.priority,
//       startDate: formData.startDate,
//       dueDate: formData.dueDate,
//       assignedUsers: formData.assignedUsers.map(userId => ({
//         userId,
//         name: employees.find(e => e._id === userId)?.name || 'Unknown'
//       }))
//     };
    
//     onCreate(taskData);
//   };

//   // Form में project select dropdown बनाएं
//   return (
//     <div className="create-task-overlay">
//       <div className="create-task-modal">
//         {/* ... existing code ... */}
        
//         <div className="form-group">
//           <label>Project *</label>
//           <select
//             name="projectId"
//             value={formData.projectId}
//             onChange={(e) => {
//               const selectedProject = projectSuggestions.find(p => p.id === e.target.value);
//               handleProjectChange(e.target.value, selectedProject?.name || '');
//             }}
//             className="filter-select"
//             required
//           >
//             <option value="">Select Project</option>
//             {projectSuggestions.map((project) => (
//               <option key={project.id} value={project.id}>
//                 {project.name}
//               </option>
//             ))}
//           </select>
//         </div>

//         {/* ... rest of the form ... */}
//       </div>
//     </div>
//   );
// };


// ✅ CREATE TASK FORM COMPONENT
const CreateTaskForm = ({ employees, projects, onCreate, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    projectName: '',
    status: 'Planning',
    priority: 'Medium',
    startDate: '',
    dueDate: '',
    assignedUsers: []
  });

  const projectSuggestions = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    
    return projects.map(project => ({
      id: project._id || project.id,
      name: project.name || project.title || 'Unnamed Project',
      client: project.client || 'No Client'
    }));
  }, [projects]);

  const handleProjectChange = (e) => {
    const selectedId = e.target.value;
    const selectedProject = projectSuggestions.find(p => p.id === selectedId);
    
    setFormData(prev => ({
      ...prev,
      projectId: selectedId,
      projectName: selectedProject?.name || ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (userId) => {
    setFormData(prev => {
      const currentValues = [...prev.assignedUsers];
      const index = currentValues.indexOf(userId);
      
      if (index === -1) {
        currentValues.push(userId);
      } else {
        currentValues.splice(index, 1);
      }
      
      return { ...prev, assignedUsers: currentValues };
    });
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
    
  //   if (!formData.title || !formData.projectId || formData.assignedUsers.length === 0) {
  //     alert('Please fill in all required fields: Title, Project, and at least one assigned user');
  //     return;
  //   }
    
  //   const taskData = {
  //     title: formData.title,
  //     description: formData.description,
  //     projectId: formData.projectId,
  //     status: formData.status,
  //     priority: formData.priority,
  //     startDate: formData.startDate,
  //     dueDate: formData.dueDate,
  //     assignedUsers: formData.assignedUsers.map(userId => ({
  //       userId,
  //       name: employees.find(e => e._id === userId)?.name || 'Unknown'
  //     }))
  //   };
    
  //   console.log('Task data to create:', taskData);
  //   onCreate(taskData);
  // };


  const handleSubmit = (e) => {
  e.preventDefault();
  
  if (!formData.title || !formData.projectId || formData.assignedUsers.length === 0) {
    alert('⚠️ Please fill in all required fields: Title, Project, and at least one assigned user');
    return;
  }
  
  // Show warning if multiple users selected
  if (formData.assignedUsers.length > 1) {
    const confirm = window.confirm(
      '⚠️ Note: Currently only one user can be assigned to a task.\n' +
      'Only the first selected user will be assigned.\n\n' +
      'Continue?'
    );
    if (!confirm) return;
  }
  
  const taskData = {
    title: formData.title,
    description: formData.description,
    projectId: formData.projectId,
    status: formData.status,
    priority: formData.priority,
    startDate: formData.startDate,
    dueDate: formData.dueDate,
    assignedUsers: formData.assignedUsers.map(userId => ({
      userId,
      name: employees.find(e => e._id === userId)?.name || 'Unknown'
    }))
  };
  
  console.log('📤 Sending task data:', taskData);
  onCreate(taskData);
};
  return (
    <div className="create-task-overlay">
      <div className="create-task-modal">
        <div className="create-task-header">
          <h2>Create New Task</h2>
          <button onClick={onClose}>✕</button>
        </div>
        
        <form className="create-task-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Task Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter task title"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter task description"
              rows="3"
            />
          </div>
          
          <div className="form-group">
            <label>Project *</label>
            <select
              name="projectId"
              value={formData.projectId}
              onChange={handleProjectChange}
              className="filter-select"
              required
            >
              <option value="">Select a Project</option>
              {projectSuggestions.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} {project.client ? `(${project.client})` : ''}
                </option>
              ))}
            </select>
            {formData.projectName && (
              <small className="selected-project">
                Selected: {formData.projectName}
              </small>
            )}
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Planning">Planning</option>
                <option value="In Progress">In Progress</option>
                <option value="Testing Phase">Testing Phase</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label>Due Date *</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Assign to Users *</label>
            <div className="users-selection-grid">
              {employees.map(employee => (
                <div 
                  key={employee._id}
                  className={`user-select-item ${formData.assignedUsers.includes(employee._id) ? 'selected' : ''}`}
                  onClick={() => handleMultiSelect(employee._id)}
                >
                  <div className="user-avatar-select">
                    {employee.name?.split(' ').map(n => n.charAt(0)).join('') || 'NA'}
                  </div>
                  <div className="user-info-select">
                    <div className="user-name-select">{employee.name}</div>
                    <div className="user-role-select">{employee.role}</div>
                  </div>
                  <div className="user-checkbox">
                    {formData.assignedUsers.includes(employee._id) ? '✓' : ''}
                  </div>
                </div>
              ))}
            </div>
            <div className="selected-count">
              Selected: {formData.assignedUsers.length} user(s)
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button"
              className="cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="submit-btn"
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ✅ EDIT TASK FORM COMPONENT
// const EditTaskForm = ({ task, employees, onUpdate, onClose }) => {
//   const [formData, setFormData] = useState({
//     title: task.title || '',
//     description: task.description || '',
//     project: task.project || '',
//     status: task.status || 'Planning',
//     priority: task.priority || 'Medium',
//     startDate: task.startDate || '',
//     dueDate: task.dueDate || '',
//     assignedUsers: task.assignedUsers?.map(u => u.userId) || []
//   });

//   const [projectSuggestions, setProjectSuggestions] = useState([]);

//   useEffect(() => {
//     if (employees && employees.length > 0) {
//       const projects = [...new Set(employees.flatMap(emp => 
//         emp.projects?.map(p => p.name) || []
//       ))];
//       setProjectSuggestions(projects);
//     }
//   }, [employees]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData(prev => ({ ...prev, [name]: value }));
//   };

//   const handleMultiSelect = (userId) => {
//     setFormData(prev => {
//       const currentValues = [...prev.assignedUsers];
//       const index = currentValues.indexOf(userId);
      
//       if (index === -1) {
//         currentValues.push(userId);
//       } else {
//         currentValues.splice(index, 1);
//       }
      
//       return { ...prev, assignedUsers: currentValues };
//     });
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
    
//     if (!formData.title || !formData.project || formData.assignedUsers.length === 0) {
//       alert('Please fill in all required fields');
//       return;
//     }
    
//     const taskData = {
//       title: formData.title,
//       description: formData.description,
//       project: formData.project,
//       status: formData.status,
//       priority: formData.priority,
//       startDate: formData.startDate,
//       dueDate: formData.dueDate,
//       assignedUsers: formData.assignedUsers.map(userId => ({
//         userId,
//         name: employees.find(e => e._id === userId)?.name || 'Unknown'
//       }))
//     };
    
//     onUpdate(taskData);
//   };

//   return (
//     <div className="create-task-overlay">
//       <div className="create-task-modal">
//         <div className="create-task-header">
//           <h2>Edit Task</h2>
//           <button onClick={onClose}>✕</button>
//         </div>
        
//         <form className="create-task-form" onSubmit={handleSubmit}>
//           <div className="form-group">
//             <label>Task Title *</label>
//             <input
//               type="text"
//               name="title"
//               value={formData.title}
//               onChange={handleInputChange}
//               placeholder="Enter task title"
//               required
//             />
//           </div>
          
//           <div className="form-group">
//             <label>Description</label>
//             <textarea
//               name="description"
//               value={formData.description}
//               onChange={handleInputChange}
//               placeholder="Enter task description"
//               rows="3"
//             />
//           </div>
          
//           <div className="form-group">
//             <label>Project Name *</label>
//             <input
//               type="text"
//               name="project"
//               value={formData.project}
//               onChange={handleInputChange}
//               placeholder="Enter project name"
//               required
//               list="project-suggestions-edit"
//             />
//             <datalist id="project-suggestions-edit">
//               {projectSuggestions.map((project, index) => (
//                 <option key={index} value={project} />
//               ))}
//             </datalist>
//           </div>
          
//           <div className="form-row">
//             <div className="form-group">
//               <label>Status</label>
//               <select
//                 name="status"
//                 value={formData.status}
//                 onChange={handleInputChange}
//               >
//                 <option value="Planning">Planning</option>
//                 <option value="In Progress">In Progress</option>
//                 <option value="Testing Phase">Testing Phase</option>
//                 <option value="Completed">Completed</option>
//                 <option value="Blocked">Blocked</option>
//                 <option value="On Hold">On Hold</option>
//               </select>
//             </div>
            
//             <div className="form-group">
//               <label>Priority</label>
//               <select
//                 name="priority"
//                 value={formData.priority}
//                 onChange={handleInputChange}
//               >
//                 <option value="Low">Low</option>
//                 <option value="Medium">Medium</option>
//                 <option value="High">High</option>
//               </select>
//             </div>
//           </div>
          
//           <div className="form-row">
//             <div className="form-group">
//               <label>Start Date</label>
//               <input
//                 type="date"
//                 name="startDate"
//                 value={formData.startDate}
//                 onChange={handleInputChange}
//               />
//             </div>
            
//             <div className="form-group">
//               <label>Due Date *</label>
//               <input
//                 type="date"
//                 name="dueDate"
//                 value={formData.dueDate}
//                 onChange={handleInputChange}
//                 required
//               />
//             </div>
//           </div>
          
//           <div className="form-group">
//             <label>Assign to Users *</label>
//             <div className="users-selection-grid">
//               {employees.map(employee => (
//                 <div 
//                   key={employee._id}
//                   className={`user-select-item ${formData.assignedUsers.includes(employee._id) ? 'selected' : ''}`}
//                   onClick={() => handleMultiSelect(employee._id)}
//                 >
//                   <div className="user-avatar-select">
//                     {employee.name?.split(' ').map(n => n.charAt(0)).join('') || 'NA'}
//                   </div>
//                   <div className="user-info-select">
//                     <div className="user-name-select">{employee.name}</div>
//                     <div className="user-role-select">{employee.role}</div>
//                   </div>
//                   <div className="user-checkbox">
//                     {formData.assignedUsers.includes(employee._id) ? '✓' : ''}
//                   </div>
//                 </div>
//               ))}
//             </div>
//             <div className="selected-count">
//               Selected: {formData.assignedUsers.length} user(s)
//             </div>
//           </div>
          
//           <div className="form-actions">
//             <button 
//               type="button"
//               className="cancel-btn"
//               onClick={onClose}
//             >
//               Cancel
//             </button>
//             <button 
//               type="submit"
//               className="submit-btn"
//             >
//               Update Task
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };



// ✅ EDIT TASK FORM COMPONENT
const EditTaskForm = ({ task, employees, projects, onUpdate, onClose }) => {
  const [formData, setFormData] = useState({
    title: task.title || '',
    description: task.description || '',
    projectId: task.projectId || task.project || '',
    projectName: task.projectName || task.project || '',
    status: task.status || 'Planning',
    priority: task.priority || 'Medium',
    startDate: task.startDate || '',
    dueDate: task.dueDate || '',
    assignedUsers: task.assignedUsers?.map(u => u.userId || u._id) || []
  });

  const projectSuggestions = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    
    return projects.map(project => ({
      id: project._id || project.id,
      name: project.name || project.title || 'Unnamed Project',
      client: project.client || 'No Client'
    }));
  }, [projects]);

  const handleProjectChange = (e) => {
    const selectedId = e.target.value;
    const selectedProject = projectSuggestions.find(p => p.id === selectedId);
    
    setFormData(prev => ({
      ...prev,
      projectId: selectedId,
      projectName: selectedProject?.name || ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleMultiSelect = (userId) => {
    setFormData(prev => {
      const currentValues = [...prev.assignedUsers];
      const index = currentValues.indexOf(userId);
      
      if (index === -1) {
        currentValues.push(userId);
      } else {
        currentValues.splice(index, 1);
      }
      
      return { ...prev, assignedUsers: currentValues };
    });
  };

  // const handleSubmit = (e) => {
  //   e.preventDefault();
    
  //   if (!formData.title || !formData.projectId || formData.assignedUsers.length === 0) {
  //     alert('Please fill in all required fields');
  //     return;
  //   }
    
  //   const taskData = {
  //     title: formData.title,
  //     description: formData.description,
  //     projectId: formData.projectId,
  //     status: formData.status,
  //     priority: formData.priority,
  //     startDate: formData.startDate,
  //     dueDate: formData.dueDate,
  //     assignedUsers: formData.assignedUsers.map(userId => ({
  //       userId,
  //       name: employees.find(e => e._id === userId)?.name || 'Unknown'
  //     }))
  //   };
    
  //   console.log('Task data to update:', taskData);
  //   onUpdate(taskData);
  // };


const handleSubmit = (e) => {
  e.preventDefault();
  
  if (!formData.title || !formData.projectId || formData.assignedUsers.length === 0) {
    alert('⚠️ Please fill in all required fields');
    return;
  }
  
  // Show warning if multiple users selected
  if (formData.assignedUsers.length > 1) {
    const confirm = window.confirm(
      '⚠️ Note: Currently only one user can be assigned to a task.\n' +
      'Only the first selected user will be assigned.\n\n' +
      'Continue?'
    );
    if (!confirm) return;
  }
  
  const taskData = {
    title: formData.title,
    description: formData.description,
    projectId: formData.projectId,
    status: formData.status,
    priority: formData.priority,
    startDate: formData.startDate,
    dueDate: formData.dueDate,
    assignedUsers: formData.assignedUsers.map(userId => ({
      userId,
      name: employees.find(e => e._id === userId)?.name || 'Unknown'
    }))
  };
  
  console.log('📤 Sending update data:', taskData);
  onUpdate(taskData);
};

  return (
    <div className="create-task-overlay">
      <div className="create-task-modal">
        <div className="create-task-header">
          <h2>Edit Task</h2>
          <button onClick={onClose}>✕</button>
        </div>
        
        <form className="create-task-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Task Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter task title"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter task description"
              rows="3"
            />
          </div>
          
          <div className="form-group">
            <label>Project *</label>
            <select
              name="projectId"
              value={formData.projectId}
              onChange={handleProjectChange}
              className="filter-select"
              required
            >
              <option value="">Select a Project</option>
              {projectSuggestions.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} {project.client ? `(${project.client})` : ''}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Planning">Planning</option>
                <option value="In Progress">In Progress</option>
                <option value="Testing Phase">Testing Phase</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="form-group">
              <label>Due Date *</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Assign to Users *</label>
            <div className="users-selection-grid">
              {employees.map(employee => (
                <div 
                  key={employee._id}
                  className={`user-select-item ${formData.assignedUsers.includes(employee._id) ? 'selected' : ''}`}
                  onClick={() => handleMultiSelect(employee._id)}
                >
                  <div className="user-avatar-select">
                    {employee.name?.split(' ').map(n => n.charAt(0)).join('') || 'NA'}
                  </div>
                  <div className="user-info-select">
                    <div className="user-name-select">{employee.name}</div>
                    <div className="user-role-select">{employee.role}</div>
                  </div>
                  <div className="user-checkbox">
                    {formData.assignedUsers.includes(employee._id) ? '✓' : ''}
                  </div>
                </div>
              ))}
            </div>
            <div className="selected-count">
              Selected: {formData.assignedUsers.length} user(s)
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              type="button"
              className="cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit"
              className="submit-btn"
            >
              Update Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ✅ TASK DETAILS MODAL COMPONENT
const TaskDetailsModal = ({ task, employees, onClose, onStatusUpdate }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [newStatus, setNewStatus] = useState(task.status || 'Pending');

  const handleStatusUpdate = () => {
    if (newStatus !== task.status) {
      onStatusUpdate(task._id, newStatus);
    }
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content task-details-modal">
        <div className="modal-header">
          <h2>Task Details</h2>
          <button onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-tabs">
          <button 
            className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button 
            className={`tab-btn ${activeTab === 'activity' ? 'active' : ''}`}
            onClick={() => setActiveTab('activity')}
          >
            Activity
          </button>
        </div>
        
        <div className="modal-body">
          {activeTab === 'details' ? (
            <div className="task-details">
              <div className="detail-row">
                <span className="detail-label">Title:</span>
                <span className="detail-value">{task.title}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Project:</span>
                <span className="detail-value">{task.project}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Description:</span>
                <span className="detail-value">{task.description || 'No description'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <select 
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="status-select"
                >
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Testing Phase">Testing Phase</option>
                  <option value="Completed">Completed</option>
                  <option value="Blocked">Blocked</option>
                  <option value="On Hold">On Hold</option>
                </select>
              </div>
              <div className="detail-row">
                <span className="detail-label">Priority:</span>
                <span className="detail-value">{task.priority}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Due Date:</span>
                <span className="detail-value">
                  {new Date(task.dueDate).toLocaleDateString()}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Progress:</span>
                <span className="detail-value">{task.progress || 0}%</span>
              </div>
              
              <div className="assigned-users-section">
                <h4>Assigned Users</h4>
                <div className="users-list">
                  {task.assignedUsers && task.assignedUsers.map((user, index) => {
                    const employee = employees.find(e => e._id === user.userId);
                    return (
                      <div key={index} className="user-item">
                        <span className="user-avatar">
                          {employee?.name ? employee.name.split(' ').map(n => n.charAt(0)).join('') : 'NA'}
                        </span>
                        <span className="user-name">{employee?.name || user.name || 'Unknown User'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="activity-log">
              <p>Activity log will be displayed here</p>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {newStatus !== task.status && (
            <button className="btn-primary" onClick={handleStatusUpdate}>
              Update Status
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ✅ EMPLOYEE DETAILS MODAL COMPONENT
const EmployeeDetailsModal = ({ employee, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content employee-details-modal">
        <div className="modal-header">
          <h2>Employee Details</h2>
          <button onClick={onClose}>✕</button>
        </div>
        
        <div className="modal-body">
          <div className="employee-header">
            <div className="employee-avatar-large">
              {employee.name?.split(' ').map(n => n.charAt(0)).join('') || 'NA'}
            </div>
            <div className="employee-info">
              <h3>{employee.name}</h3>
              <p className="employee-role">{employee.role}</p>
              <p className="employee-email">{employee.email}</p>
            </div>
          </div>
          
          <div className="employee-details">
            <div className="detail-section">
              <h4>Contact Information</h4>
              <div className="detail-row">
                <span className="detail-label">Employee ID:</span>
                <span className="detail-value">{employee.employeeId || employee._id}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Department:</span>
                <span className="detail-value">{employee.department || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{employee.phone || 'Not available'}</span>
              </div>
            </div>
            
            <div className="detail-section">
              <h4>Current Projects</h4>
              {employee.projects && employee.projects.length > 0 ? (
                <ul>
                  {employee.projects.map((project, index) => (
                    <li key={index}>{project.name} - {project.role}</li>
                  ))}
                </ul>
              ) : (
                <p>No projects assigned</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TasksPage;